import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { uploadOrgLogo, setOrgLogoUrl, removeOrgLogo } from '@/services/organizations.service';
import { sanitizeSupabaseError } from '@/lib/errorHandler';

// O logo aceita SVG (ao contrário dos avatares de utilizador) — é carregado pelo
// admin e mostrado apenas na sua própria org. Limite de 2MB.
const TIPOS_LOGO = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
const TAMANHO_MAX = 2 * 1024 * 1024; // 2MB

/**
 * Mutações de logo da organização (carregar / remover). Após sucesso,
 * refresca a org no AuthContext para a sidebar refletir a mudança.
 */
export function useOrgLogo() {
  const { t } = useTranslation();
  const { organization, refreshOrganization } = useAuthContext();

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!organization) throw new Error('no-org');
      const url = await uploadOrgLogo({ orgId: organization.id, file });
      await setOrgLogoUrl({ logoUrl: url });
    },
    onSuccess: async () => {
      await refreshOrganization();
      toast.success(t('settings.logo.uploadedToast'));
    },
    onError: (error) => toast.error(sanitizeSupabaseError(error)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!organization) throw new Error('no-org');
      await removeOrgLogo({ orgId: organization.id, logoUrl: organization.logo_url });
    },
    onSuccess: async () => {
      await refreshOrganization();
      toast.success(t('settings.logo.removedToast'));
    },
    onError: (error) => toast.error(sanitizeSupabaseError(error)),
  });

  const uploadLogo = (file: File) => {
    if (!TIPOS_LOGO.includes(file.type)) {
      toast.error(t('settings.logo.invalidType'));
      return;
    }
    if (file.size > TAMANHO_MAX) {
      toast.error(t('settings.logo.tooLarge'));
      return;
    }
    upload.mutate(file);
  };

  return {
    uploadLogo,
    removeLogo: () => remove.mutate(),
    isUploading: upload.isPending,
    isRemoving: remove.isPending,
  };
}
